import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Premium list shell: rounded surface, soft divider lines, no harsh table grid.
 * Each row stays self-contained — the parent just supplies padding via
 * `className` if needed.
 */
export function DataList({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border/70 bg-card shadow-xs',
        'divide-y divide-border/60',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Optional list header — small uppercase labels, no border. Drop above the
 * first DataListRow when columns warrant labels.
 */
export function DataListHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-5 py-2.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * A row. The `as` prop lets you switch between a plain div and a link without
 * losing the styling. Keeps generous padding, hover affordance.
 */
export function DataListRow({
  children,
  className,
  interactive = true,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  /** When true, hover bg + cursor-pointer. Set false for non-clickable rows. */
  interactive?: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'group/row flex items-center gap-4 px-5 py-3.5',
        interactive && 'cursor-pointer transition-colors hover:bg-muted/40',
        className,
      )}
    >
      {children}
    </div>
  );
}
